import { Helmet } from "react-helmet-async";
import { useParams } from "react-router";
import Image from "../components/Image";
import Loading from "../components/Loading";
import MarkDown from "../components/MarkDown";
import useArticle from "../hooks/useArticle";
import usePageReset from "../hooks/usePageReset";
import structuredData from "../assets/structuredText";
import usePersonProfile from "../hooks/usePersonProfile";
import { Link } from "react-router-dom";

export default function Article() {
  const { articleID } = useParams();
  const [post, isLoading, authorId] = useArticle(articleID as string);
  const [authorName, isLoadingAuthor] = usePersonProfile(
    authorId,
    Boolean(authorId)
  );
  usePageReset();

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta
          name="keywords"
          content={`${post?.title} blog post article cultiv`}
        />
        <meta
          name="description"
          content={`This is an article about ${post?.title}`}
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:url" content="https://cultiv.llc" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          itemProp="image"
          content={
            post ? post.post_thumbnail : "https://cultiv.llc/images/metaimg.jpg"
          }
        />
        <link
          rel="image_src"
          itemProp="image"
          href={
            post ? post.post_thumbnail : "https://cultiv.llc/images/metaimg.jpg"
          }
        />
        <meta
          property="og:description"
          content={`This is an article about ${post?.title} `}
        />
        <meta property="og:site_name" content="Cultiv LLC" />
        <meta
          property="og:title"
          content={post ? post.title : "Cultiv | Article"}
        />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://cultiv.llc" />
        <meta property="twitter:title" content="Cultiv | Article" />
        <meta
          property="twitter:description"
          content={`This is an article about ${post?.title} `}
        />
        <meta
          property="twitter:image"
          content="https://cultiv.llc/images/metaimg.jpg"
        />
        <link rel="canonical" href="https://cultiv.llc" />
        <script type="application/ld+json">{structuredData}</script>

        <title>Cultiv | {`${post ? post.title : "Article"}`}</title>
      </Helmet>

      {isLoading ? (
        <div className="flex justify-center items-center h-0 min-h-screen">
          <Loading />
        </div>
      ) : (
        <div className="w-full grid grid-cols-4 lg:grid-cols-12 gap-4 lg:gap-8 justify-start items-start md:px-15 lg:px-20 xl:px-30 px-6 pt-8 lg:pt-15">
          <div className="flex flex-col col-span-full lg:col-span-10 gap-3 w-full lg:col-start-2 pb-3 lg:pb-8 border-b-2 border-gray-400">
            <h6 className="font-condensedThin text-base md:text-xl lg:text-3xl">
              {post?.post_meta?.subtitle}
            </h6>
            <h1 className="text-xl md:text-2xl lg:text-4xl font-bold font-expandedBold">
              {post?.title}
            </h1>
            <h3
              className={`text-xs md:text-base lg:text-lg font-condensedItalic text-cultiv-grey transition-opacity duration-300 ease-in ${
                isLoadingAuthor ? "opacity-0" : "opacity-100"
              }`}
            >
              <Link
                to={`/profile/${authorName?.idea_name}`}
                className="text-cultiv-blue underline decoration-2 decoration-white hover:decoration-cultiv-blue transition-all duration-500 ease-in-out underline-offset-4 will-change-transform"
              >
                {authorName?.name}
              </Link>
              {` , ${post?.created_at}`}
            </h3>
          </div>
          <div className="flex flex-col justify-center col-span-full lg:col-span-10 lg:col-start-2">
            <Image
              src={post?.post_cover}
              width={1120}
              height={654}
              className="object-cover self-center w-full mb-3 lg:mb-10"
              alt={post?.title}
            />
            <MarkDown articleBody={post?.md_text} />
          </div>
        </div>
      )}
    </>
  );
}
